import { SelfContainedCommand, CommandExecutionContext } from "../../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../../types/standardToolResults";
import { createFileLogger } from "../../../utils/logger";

const log = createFileLogger('BROWSER_OBSERVE_PAGE');

export const browserObservePageCommand: SelfContainedCommand = {
  id: 'browser_observe_page',
  description: `Ask the user to observe and report information from the current browser page.

This enables the AI to gather information from the page by asking the user specific
questions about what they see. This approach works with any website without needing
to parse HTML or maintain selectors.

**Use Cases:**
• Get the amount due from a payment page
• Check if a confirmation message appeared
• Read a receipt number
• Verify current page content
• Get form values or status information

**Benefits:**
• Works with any website layout
• No need for page parsing
• User verifies information accuracy
• Handles dynamic content naturally`,
  autonomyLevel: 'ask',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'question',
        type: 'string',
        description: 'Question to ask the user (e.g., "What is the registration amount due?")',
        required: true
      },
      {
        name: 'expectedFormat',
        type: 'string',
        description: 'Expected answer format (e.g., "dollar amount", "yes/no", "receipt number")',
        required: false
      },
      {
        name: 'browserWindowId',
        type: 'string',
        description: 'Specific browser window ID (optional)',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { 
          question: 'What is the registration amount due?',
          expectedFormat: 'dollar amount'
        },
        expectedOutput: createSuccessResult('User provided observation', {
          question: 'What is the registration amount due?',
          userResponse: '$312'
        }),
        description: 'Get payment amount from page',
        context: 'During payment workflow'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 5000,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { question, expectedFormat, browserWindowId } = params;
    
    try {
      log.info('[browser_observe_page] Asking user:', { question, expectedFormat });
      
      if (!question) {
        return createErrorResult(
          'Missing question',
          'question parameter is required',
          'validation',
          { data: params }
        );
      }
      
      return createSuccessResult(
        'User provided observation',
        {
          question,
          expectedFormat,
          browserWindowId,
          userResponse: '',
          timestamp: new Date().toISOString()
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            requiresUserInteraction: true
          } as any
        }
      );
      
    } catch (error: any) {
      log.error('[browser_observe_page] Error:', error);
      return createErrorResult(
        'Page observation failed',
        `Failed to get user observation: ${error.message || 'Unknown error'}`,
        'execution',
        {
          data: { question, expectedFormat },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

